import { MusicCard, SerenityCard, WisdomCard } from '@components/Cards';
import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useFavorites } from '@hooks/useFavorites';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { placeHolderData } from '../../config';
import {
  getLeaderboardAlbums,
  getLeaderboardTracks,
  getTrackById,
} from '../../services/api';
import { Album } from '../../types';
import { getTrackUrlForLocale } from '../../utils/localeUtils';

type FilterTab = 'all' | 'tracks' | 'music' | 'serenity' | 'book';

interface CategoryState {
  items: Album[];
  loading: boolean;
  hasMore: boolean;
  offset: number;
  total: number;
}

interface TrackItem {
  id: string;
  trackName: string;
  genre: string[];
  album: {
    id: string;
    name: string;
    thumbnailUrl: string;
    category: string;
  };
  contributors: Array<{
    id: string;
    givenName: string;
    thumbnailUrl?: string;
    videoMobileUrl?: string;
    videoMobilePreviewUrl?: string;
  }>;
  audioUrl: {
    urls: Array<{
      url: string;
      locale: string;
      extension: string;
    }>;
  };
  likes: number;
  plays: number;
  description?: string;
  isLiked?: boolean;
}

interface TracksState {
  items: TrackItem[];
  loading: boolean;
  hasMore: boolean;
  offset: number;
  total: number;
}

const LIMIT = 12;

export const LeaderboardPage = memo(() => {
  const { t } = useTranslation();
  const { setActivePopup } = useCommonState();
  const { setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { toggleFavorite } = useFavorites();
  const { loadAlbumById } = useAlbumData();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [loadingAlbumId, setLoadingAlbumId] = useState<string | null>(null);

  // Category states
  const [musicAlbums, setMusicAlbums] = useState<CategoryState>({
    items: [],
    loading: false,
    hasMore: true,
    offset: 0,
    total: 0,
  });
  const [serenityAlbums, setSerenityAlbums] = useState<CategoryState>({
    items: [],
    loading: false,
    hasMore: true,
    offset: 0,
    total: 0,
  });
  const [bookAlbums, setBookAlbums] = useState<CategoryState>({
    items: [],
    loading: false,
    hasMore: true,
    offset: 0,
    total: 0,
  });

  // Tracks state (all tracks for "All" tab)
  const [tracks, setTracks] = useState<TracksState>({
    items: [],
    loading: false,
    hasMore: true,
    offset: 0,
    total: 0,
  });

  // Category-specific tracks states
  const [musicTracks, setMusicTracks] = useState<TracksState>({
    items: [],
    loading: false,
    hasMore: true,
    offset: 0,
    total: 0,
  });
  const [serenityTracks, setSerenityTracks] = useState<TracksState>({
    items: [],
    loading: false,
    hasMore: true,
    offset: 0,
    total: 0,
  });
  const [bookTracks, setBookTracks] = useState<TracksState>({
    items: [],
    loading: false,
    hasMore: true,
    offset: 0,
    total: 0,
  });

  // Loading state for playing track
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null);

  const fetchTracks = useCallback(
    async (offset: number, append: boolean = false) => {
      setTracks((prev) => ({ ...prev, loading: true }));

      try {
        const result = await getLeaderboardTracks(LIMIT, offset);
        const newItems = result.items || [];
        const hasMore = result.pagination?.hasNext || false;
        const total = result.pagination?.total || 0;

        setTracks((prev) => ({
          items: append ? [...prev.items, ...newItems] : newItems,
          loading: false,
          hasMore,
          offset: offset + newItems.length,
          total,
        }));
      } catch (error) {
        console.error('Error fetching tracks:', error);
        setTracks((prev) => ({ ...prev, loading: false }));
      }
    },
    [],
  );

  // Fetch tracks for a specific category
  const fetchTracksForCategory = useCallback(
    async (
      category: 'music' | 'serenity' | 'book',
      offset: number,
      append: boolean = false,
    ) => {
      const setState =
        category === 'music'
          ? setMusicTracks
          : category === 'serenity'
            ? setSerenityTracks
            : setBookTracks;

      setState((prev) => ({ ...prev, loading: true }));

      try {
        const result = await getLeaderboardTracks(LIMIT, offset, category);
        const newItems = result.items || [];
        const hasMore = result.pagination?.hasNext || false;
        const total = result.pagination?.total || 0;

        setState((prev) => ({
          items: append ? [...prev.items, ...newItems] : newItems,
          loading: false,
          hasMore,
          offset: offset + newItems.length,
          total,
        }));
      } catch (error) {
        console.error(`Error fetching ${category} tracks:`, error);
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [],
  );

  const fetchAlbumsForCategory = useCallback(
    async (
      category: 'music' | 'serenity' | 'book',
      offset: number,
      append: boolean = false,
    ) => {
      const setState =
        category === 'music'
          ? setMusicAlbums
          : category === 'serenity'
            ? setSerenityAlbums
            : setBookAlbums;

      setState((prev) => ({ ...prev, loading: true }));

      try {
        const result = await getLeaderboardAlbums(category, LIMIT, offset);
        const newItems = result.items || [];
        const hasMore = result.pagination?.hasNext || false;
        const total = result.pagination?.total || 0;

        setState((prev) => ({
          items: append ? [...prev.items, ...newItems] : newItems,
          loading: false,
          hasMore,
          offset: offset + LIMIT,
          total,
        }));
      } catch (error) {
        console.error(`Error fetching ${category} leaderboard albums:`, error);
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [],
  );

  // Initial load
  useEffect(() => {
    // Fetch all tracks (for All tab)
    fetchTracks(0, false);
    // Fetch albums for each category
    fetchAlbumsForCategory('music', 0, false);
    fetchAlbumsForCategory('serenity', 0, false);
    fetchAlbumsForCategory('book', 0, false);
    // Fetch tracks for each category
    fetchTracksForCategory('music', 0, false);
    fetchTracksForCategory('serenity', 0, false);
    fetchTracksForCategory('book', 0, false);
  }, [fetchAlbumsForCategory, fetchTracks, fetchTracksForCategory]);

  // Reset scroll position when tab changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [activeTab]);

  const handleAlbumClick = useCallback(
    async (album: Album) => {
      if (!album?.id) return;
      setLoadingAlbumId(album.id);
      try {
        const fullAlbum = await loadAlbumById(album.id);
        if (fullAlbum && fullAlbum.tracks?.length > 0) {
          const firstTrack = fullAlbum.tracks[0];
          const trackUrl = getTrackUrlForLocale(firstTrack, preferredLocale);
          if (trackUrl) {
            const contributor = fullAlbum.contributors?.[0];
            // Create recent track object
            const recentTrack = {
              track: {
                name: firstTrack.name,
                url: trackUrl,
                trackIndex: 1,
              },
              contributor: {
                id: contributor?.id || fullAlbum.id,
                givenName: contributor?.givenName || t('common.unknownArtist'),
                thumbnailUrl:
                  contributor?.thumbnailUrl ||
                  placeHolderData.contributorThumbnail,
              },
              albumArtUrl:
                fullAlbum.albumArtUrl ||
                contributor?.thumbnailUrl ||
                placeHolderData.albumCover,
              timestamp: Date.now(),
            };
            addRecentTrack(recentTrack);
            setActivePlaylist({ album: fullAlbum, currentIndex: 0 });
            setActivePopup('musicplayer');
          }
        }
      } catch (error) {
        console.error('Error loading album:', error);
      } finally {
        setLoadingAlbumId(null);
      }
    },
    [
      loadAlbumById,
      setActivePlaylist,
      addRecentTrack,
      setActivePopup,
      preferredLocale,
    ],
  );

  // Helper function to update album liked status in local state
  const updateLocalAlbumLikedStatus = useCallback(
    (albumId: string, isLiked: boolean) => {
      // Update in music albums
      setMusicAlbums((prev) => ({
        ...prev,
        items: prev.items.map((album) =>
          album.id === albumId ? { ...album, isLiked } : album,
        ),
      }));
      // Update in serenity albums
      setSerenityAlbums((prev) => ({
        ...prev,
        items: prev.items.map((album) =>
          album.id === albumId ? { ...album, isLiked } : album,
        ),
      }));
      // Update in book albums
      setBookAlbums((prev) => ({
        ...prev,
        items: prev.items.map((album) =>
          album.id === albumId ? { ...album, isLiked } : album,
        ),
      }));
    },
    [],
  );

  const handleFavoriteClick = useCallback(
    async (e: React.MouseEvent, album: Album) => {
      e.stopPropagation();
      const newIsLiked = !album.isLiked;
      // Optimistic update
      updateLocalAlbumLikedStatus(album.id, newIsLiked);
      try {
        await toggleFavorite(
          'album',
          album.id,
          album.isLiked,
          album.albumName,
          album.contributors?.[0]?.givenName,
          album.category,
        );
      } catch {
        // Revert on error
        updateLocalAlbumLikedStatus(album.id, album.isLiked || false);
      }
    },
    [toggleFavorite, updateLocalAlbumLikedStatus],
  );

  // Helper function to update track liked status in local state
  const updateLocalTrackLikedStatus = useCallback(
    (trackId: string, isLiked: boolean) => {
      // Update in all tracks (for All tab)
      setTracks((prev) => ({
        ...prev,
        items: prev.items.map((track) =>
          track.id === trackId ? { ...track, isLiked } : track,
        ),
      }));
      // Update in music tracks
      setMusicTracks((prev) => ({
        ...prev,
        items: prev.items.map((track) =>
          track.id === trackId ? { ...track, isLiked } : track,
        ),
      }));
      // Update in serenity tracks
      setSerenityTracks((prev) => ({
        ...prev,
        items: prev.items.map((track) =>
          track.id === trackId ? { ...track, isLiked } : track,
        ),
      }));
      // Update in book tracks
      setBookTracks((prev) => ({
        ...prev,
        items: prev.items.map((track) =>
          track.id === trackId ? { ...track, isLiked } : track,
        ),
      }));
    },
    [],
  );

  // Handle track favorite click
  const handleTrackFavoriteClick = useCallback(
    async (e: React.MouseEvent, track: TrackItem) => {
      e.stopPropagation();
      const newIsLiked = !track.isLiked;
      // Optimistic update
      updateLocalTrackLikedStatus(track.id, newIsLiked);
      try {
        await toggleFavorite(
          'track',
          track.id,
          track.isLiked,
          track.trackName,
          track.contributors?.[0]?.givenName,
          track.album?.category,
        );
      } catch {
        // Revert on error
        updateLocalTrackLikedStatus(track.id, track.isLiked || false);
      }
    },
    [toggleFavorite, updateLocalTrackLikedStatus],
  );

  // Handle track click - play the track (similar to Favorites page)
  const handleTrackClick = useCallback(
    async (track: TrackItem) => {
      if (!track?.id) return;
      setLoadingTrackId(track.id);

      try {
        const contributor = track.contributors?.[0];
        const trackInfo = await getTrackById(track.id);

        if (trackInfo) {
          // Build track with urls for getTrackUrlForLocale
          const trackWithUrls = {
            id: track.id,
            name: track.trackName,
            duration: 0,
            isLiked: false,
            urls: trackInfo.audioUrl?.urls || track.audioUrl?.urls || [],
            url:
              trackInfo.audioUrl?.urls && trackInfo.audioUrl.urls.length > 0
                ? undefined
                : trackInfo.audioUrl?.path +
                  '/' +
                  trackInfo.audioUrl?.fileName +
                  '.mp3',
          };

          // Create a single-track album playlist
          const singleTrackAlbum = {
            id: track.album?.id || track.id,
            albumName: track.trackName,
            albumArtUrl: track.album?.thumbnailUrl,
            contributors: track.contributors,
            tracks: [trackWithUrls],
            category: track.album?.category || 'music',
            likes: track.likes || 0,
            isLiked: false,
            upc: '',
            pk: '',
            description: track.description || '',
            genre: track.genre || [],
            type: 'track',
          };

          const playlist = {
            currentIndex: 0,
            album: singleTrackAlbum,
          };

          // Create recent track object
          const recentTrack = {
            track: {
              name: track.trackName,
              url: getTrackUrlForLocale(trackWithUrls as any, preferredLocale),
              trackIndex: 1,
            },
            contributor: {
              id: contributor?.id || track.id,
              givenName: contributor?.givenName || t('common.unknownArtist'),
              thumbnailUrl:
                contributor?.thumbnailUrl ||
                placeHolderData.contributorThumbnail,
            },
            albumArtUrl:
              track.album?.thumbnailUrl ||
              contributor?.thumbnailUrl ||
              placeHolderData.albumCover,
            timestamp: Date.now(),
          };

          setActivePopup('musicplayer');
          addRecentTrack(recentTrack);
          setActivePlaylist(playlist as any);
        }
      } catch (error) {
        console.error('Error playing track:', error);
      } finally {
        setLoadingTrackId(null);
      }
    },
    [setActivePlaylist, addRecentTrack, setActivePopup, preferredLocale],
  );

  const getCategoryState = (category: 'music' | 'serenity' | 'book') => {
    return category === 'music'
      ? musicAlbums
      : category === 'serenity'
        ? serenityAlbums
        : bookAlbums;
  };

  const isInitialLoading =
    tracks.loading &&
    tracks.items.length === 0 &&
    musicAlbums.loading &&
    musicAlbums.items.length === 0 &&
    serenityAlbums.loading &&
    serenityAlbums.items.length === 0 &&
    bookAlbums.loading &&
    bookAlbums.items.length === 0;

  const filterTabs = [
    { id: 'all' as FilterTab, label: t('leaderboard.tabAll') },
    {
      id: 'tracks' as FilterTab,
      label: t('leaderboard.tabTracks'),
      count: musicTracks.total + serenityTracks.total + bookTracks.total,
    },
    { id: 'music' as FilterTab, label: t('leaderboard.tabMusic'), count: musicAlbums.total },
    {
      id: 'serenity' as FilterTab,
      label: t('leaderboard.tabSerenity'),
      count: serenityAlbums.total,
    },
    { id: 'book' as FilterTab, label: t('leaderboard.tabAudiobooks'), count: bookAlbums.total },
  ];

  const renderAlbumCard = (album: Album) => {
    const contributor = album.contributors?.[0];
    const posterUrl =
      album.videoMobilePreviewUrl ||
      contributor?.videoMobilePreviewUrl ||
      contributor?.thumbnailUrl;

    const commonProps = {
      key: album.id,
      album,
      contributor,
      albumName: album.albumName,
      likes: album.likes,
      isLiked: album.isLiked,
      onClick: () => handleAlbumClick(album),
      onFavoriteClick: (e: React.MouseEvent) => handleFavoriteClick(e, album),
      showLikesCount: true,
    };

    // Use different card component based on album category
    if (album.category === 'music') {
      return (
        <MusicCard
          {...commonProps}
          videoUrl={album.videoMobileUrl || contributor?.videoMobileUrl}
          posterUrl={posterUrl}
          albumArtUrl={album.albumArtUrl}
          showFavoriteButton={true}
        />
      );
    } else if (album.category === 'serenity') {
      return (
        <SerenityCard
          {...commonProps}
          videoUrl={album.videoMobileUrl || contributor?.videoMobileUrl}
          posterUrl={posterUrl}
        />
      );
    } else {
      // book/wisdom category
      return <WisdomCard {...commonProps} albumArtUrl={album.albumArtUrl} />;
    }
  };

  // Render a single track card using category-based card components
  const renderTrackCard = (track: TrackItem) => {
    const contributor = track.contributors?.[0];
    // Normalize category to lowercase and handle variations
    const rawCategory = track.album?.category?.toLowerCase() || 'music';
    const category =
      rawCategory === 'book' ||
      rawCategory === 'audiobook' ||
      rawCategory === 'wisdom'
        ? 'book'
        : rawCategory === 'serenity' || rawCategory === 'wellness'
          ? 'serenity'
          : 'music';

    // Create a pseudo-album object for the card components
    const trackAsAlbum = {
      id: track.album.id,
      name: track.trackName, // Use track name as album name for display
      albumName: track.trackName,
      category: category,
    };

    const posterUrl =
      contributor?.videoMobilePreviewUrl || contributor?.thumbnailUrl;

    const commonProps = {
      key: track.id,
      album: trackAsAlbum,
      contributor,
      albumName: track.trackName,
      likes: track.likes,
      isLiked: track.isLiked || false,
      onClick: () => handleTrackClick(track),
      onFavoriteClick: (e: React.MouseEvent) =>
        handleTrackFavoriteClick(e, track),
      showLikesCount: true,
    };

    // Use different card component based on album category
    if (category === 'music') {
      return (
        <MusicCard
          {...commonProps}
          videoUrl={contributor?.videoMobileUrl}
          posterUrl={posterUrl}
          albumArtUrl={track.album.thumbnailUrl}
          showFavoriteButton={true}
        />
      );
    } else if (category === 'serenity') {
      return (
        <SerenityCard
          {...commonProps}
          videoUrl={contributor?.videoMobileUrl}
          posterUrl={posterUrl}
        />
      );
    } else {
      // book/wisdom category
      return (
        <WisdomCard {...commonProps} albumArtUrl={track.album.thumbnailUrl} />
      );
    }
  };

  // Render tracks section (all tracks for All tab)
  const renderTracksSection = () => {
    if (tracks.items.length === 0 && !tracks.loading) return null;

    return (
      <div className="mb-8">
        <h2 className="text-[20px] sm:text-[24px] font-normal leading-[140%] text-white font-['DM_Serif_Text'] mb-4 text-left">
          Tracks
        </h2>
        <div className="responsive-grid gap-4">
          {tracks.items.map((track) => renderTrackCard(track))}
        </div>
        {tracks.hasMore && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => fetchTracks(tracks.offset, true)}
              disabled={tracks.loading}
              className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
              {tracks.loading ? (
                <div className="flex items-center gap-2">
                  <NapsterSpinner size="small" />
                  {t('common.loading')}
                </div>
              ) : (
                t('leaderboard.loadMoreTracks')
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Get tracks state for a specific category
  const getCategoryTracksState = (category: 'music' | 'serenity' | 'book') => {
    switch (category) {
      case 'music':
        return musicTracks;
      case 'serenity':
        return serenityTracks;
      case 'book':
        return bookTracks;
    }
  };

  // Render tracks section for a specific category
  const renderCategoryTracksSection = (
    title: string,
    category: 'music' | 'serenity' | 'book',
    showCount: boolean = true,
  ) => {
    const state = getCategoryTracksState(category);
    if (state.items.length === 0 && !state.loading) return null;

    return (
      <div className="mb-8">
        <h2 className="text-[20px] sm:text-[24px] font-normal leading-[140%] text-white font-['DM_Serif_Text'] mb-4 text-left">
          {title}
          {showCount && state.total > 0 && (
            <span className="ml-2 text-white/60 text-[16px] sm:text-[18px]">
              ({state.total})
            </span>
          )}
        </h2>
        <div className="responsive-grid gap-4">
          {state.items.map((track) => renderTrackCard(track))}
        </div>
        {state.hasMore && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() =>
                fetchTracksForCategory(category, state.offset, true)
              }
              disabled={state.loading}
              className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
              {state.loading ? (
                <div className="flex items-center gap-2">
                  <NapsterSpinner size="small" />
                  {t('common.loading')}
                </div>
              ) : (
                t('leaderboard.loadMore', { title })
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCategorySection = (
    title: string,
    category: 'music' | 'serenity' | 'book',
    state: CategoryState,
    showCount: boolean = false,
  ) => {
    if (state.items.length === 0 && !state.loading) return null;

    return (
      <div className="mb-8">
        <h2 className="text-[20px] sm:text-[24px] font-normal leading-[140%] text-white font-['DM_Serif_Text'] mb-4 text-left">
          {title}
          {showCount && state.total > 0 && (
            <span className="ml-2 text-white/60 text-[16px] sm:text-[18px]">
              ({state.total})
            </span>
          )}
        </h2>
        <div className="responsive-grid gap-4">
          {state.items.map((album) => renderAlbumCard(album))}
        </div>
        {state.hasMore && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() =>
                fetchAlbumsForCategory(category, state.offset, true)
              }
              disabled={state.loading}
              className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
              {state.loading ? (
                <div className="flex items-center gap-2">
                  <NapsterSpinner size="small" />
                  {t('common.loading')}
                </div>
              ) : (
                t('leaderboard.loadMore', { title })
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderSingleCategory = () => {
    const category = activeTab as 'music' | 'serenity' | 'book';
    const albumState = getCategoryState(category);
    const tracksState = getCategoryTracksState(category);
    const title =
      category === 'music'
        ? t('leaderboard.tabMusic')
        : category === 'serenity'
          ? t('leaderboard.tabSerenity')
          : t('leaderboard.tabAudiobooks');

    const hasNoContent =
      albumState.items.length === 0 &&
      !albumState.loading &&
      tracksState.items.length === 0 &&
      !tracksState.loading;

    return (
      <div className="space-y-8">
        {hasNoContent ? (
          <div className="text-center py-12 text-white/60">
            No {title.toLowerCase()} content found.
          </div>
        ) : (
          <>
            {/* Albums Section */}
            {(albumState.items.length > 0 || albumState.loading) && (
              <div className="mb-8">
                {/* <h2 className="text-[20px] sm:text-[24px] font-normal leading-[140%] text-white font-['DM_Serif_Text'] mb-4 text-left">
                  Albums
                  {albumState.total > 0 && (
                    <span className="ml-2 text-white/60 text-[16px] sm:text-[18px]">
                      ({albumState.total})
                    </span>
                  )}
                </h2> */}
                <div className="responsive-grid gap-4">
                  {albumState.items.map((album) => renderAlbumCard(album))}
                </div>
                {albumState.hasMore && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={() =>
                        fetchAlbumsForCategory(
                          category,
                          albumState.offset,
                          true,
                        )
                      }
                      disabled={albumState.loading}
                      className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                      {albumState.loading ? (
                        <div className="flex items-center gap-2">
                          <NapsterSpinner size="small" />
                          {t('common.loading')}
                        </div>
                      ) : (
                        t('leaderboard.loadMoreAlbums', { title })
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Tracks Section */}
            {renderCategoryTracksSection(`${title} Tracks`, category, true)}
          </>
        )}
      </div>
    );
  };

  // Render the Tracks tab content with category-wise sections
  const renderTracksTabContent = () => {
    const hasNoTracks =
      musicTracks.items.length === 0 &&
      !musicTracks.loading &&
      serenityTracks.items.length === 0 &&
      !serenityTracks.loading &&
      bookTracks.items.length === 0 &&
      !bookTracks.loading;

    if (hasNoTracks) {
      return (
        <div className="text-center py-12 text-white/60">No tracks found.</div>
      );
    }

    return (
      <div className="space-y-8">
        {renderCategoryTracksSection('Music Tracks', 'music')}
        {renderCategoryTracksSection('Serenity & Wellness Tracks', 'serenity')}
        {renderCategoryTracksSection('Audiobook Tracks', 'book')}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full pt-0 sm:pt-[10px]">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6">
        <div className="flex flex-col">
          <h1 className="text-[32px] sm:text-[40px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
            {t('leaderboard.title')}
          </h1>
          <div className="text-[14px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
            Most favorite albums and tracks.
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-black'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}>
              {tab.label}
              {tab.id !== 'all' && tab.count !== undefined && tab.count > 0 && (
                <span className="ml-2 text-xs opacity-70">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto na-scroll-container">
        <div className="px-4 sm:px-6 pb-[125px]">
          {/* Loading State */}
          {isInitialLoading && (
            <div className="responsive-grid gap-4">
              {[...Array(8)].map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="flex flex-col animate-pulse">
                  <div className="w-full h-[520px] rounded-[16px] bg-gray-700"></div>
                </div>
              ))}
            </div>
          )}

          {/* Content when not loading */}
          {!isInitialLoading && (
            <>
              {activeTab === 'all' ? (
                <div className="space-y-8">
                  {renderTracksSection()}
                  {renderCategorySection(t('leaderboard.tabMusic'), 'music', musicAlbums)}
                  {renderCategorySection(
                    'Serenity & Wellness',
                    'serenity',
                    serenityAlbums,
                  )}
                  {renderCategorySection(t('leaderboard.tabAudiobooks'), 'book', bookAlbums)}
                </div>
              ) : activeTab === 'tracks' ? (
                renderTracksTabContent()
              ) : (
                renderSingleCategory()
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

LeaderboardPage.displayName = 'LeaderboardPage';

export default LeaderboardPage;
